import { Chain } from '@1delta/chain-registry'
import { multicallRetryUniversal } from '@1delta/providers'
import type { Address } from 'viem'
import { YieldFetcher } from '../../types'

/**
 * Parallel sUSDp — the ERC-4626 savings vault over Parallel V3's USDp
 * stablecoin (docs.parallel.best), an Angle-lineage codebase
 * (Parallelizer = Transmuter fork, Savings = `Savings.sol` fork).
 *
 * Yield is non-dilutive USDp minted at a governance rate pushed by
 * DAO-whitelisted keepers: the vault accrues
 * `totalAssets × rate × Δt / 1e27` between pokes, so `rate()` is a
 * per-second LINEAR rate in base 27 and annualises to the package-wide
 * nominal APR as `rate / 1e27 × secondsPerYear`. The compounded figure
 * DefiLlama's `parallel-protocol-v3` pools show (10.0 % vs our 9.53 %
 * at the 2026-08 integration read) is the APY and must not be emitted
 * here. There is no on-chain solvency invariant on the savings leg —
 * backing sits in the Parallelizer reserves (Spark-V2 `vsr` trust
 * class).
 *
 * The integrator docs advertise `estimatedAPR()`, but it REVERTS on
 * the deployed vaults — `rate()` is the only live rate read.
 *
 * The rate is PER-CHAIN state, but keepers set the same value on every
 * active deployment (verified 2026-08-03: 9.5311 % on Avalanche,
 * HyperEVM, Ethereum and Base alike), so the chains are tried in TVL
 * order (Avalanche 268k USDp / HyperEVM 42k / Ethereum 2.9k / Base
 * ~130) and the first successful read wins. The dormant Sonic vault
 * (`0xe8a3da…`, rate 0, ~1 USDp) is deliberately NOT in the fallback
 * list and NOT in the savings registry — under the shared yield key it
 * would either poison the global rate with 0 or be overstated at the
 * active-chain rate.
 *
 * Address-reuse trap: Parallel's deterministic deployer assigns the
 * SAME address to DIFFERENT contracts per chain — `0x9b3a8f7…` is
 * sUSDp on HyperEVM but USDp (the underlying!) on Ethereum. Never
 * assume Angle-style vanity-address consistency across chains.
 */

const RATE_ABI = [
  {
    name: 'rate',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const

/** sUSDp vaults in fallback order (largest TVL first). */
const SUSDP_VAULTS: { chain: string; address: Address }[] = [
  {
    chain: Chain.AVALANCHE_C_CHAIN,
    address: '0x9d92c21205383651610f90722131655a5b8ed3e0',
  },
  {
    chain: Chain.HYPEREVM,
    address: '0x9b3a8f7cec208e247d97dee13313690977e24459',
  },
  {
    chain: Chain.ETHEREUM_MAINNET,
    address: '0x0d45b129dc868963025db79a9074ea9c9e32cae4',
  },
  {
    chain: Chain.BASE,
    address: '0x472ed57b376fe400259fb28e5c46eb53f0e3e7e7',
  },
]

const YEAR_SECONDS = 31_536_000
const BASE_27 = 1e27

/** Per-second base-27 linear rate → nominal APR percent. */
export const aprFromRate = (rate: bigint): number =>
  (Number(rate) / BASE_27) * YEAR_SECONDS * 100

// Keys. `SUSDP_GROUP_KEY` is the **verified** token-list `assetGroup` —
// identical on 43114/999/8453 (checked 2026-08) — which the lending
// join (`intrinsicYields[asset.assetGroup]`) reads, e.g. for the Silo
// V2 sUSDp markets on Avalanche. The bare key follows the
// registry-binding convention (WSTETH / SFRXUSD / …). NOT to be
// confused with Base's unrelated `BaseUSDP::USDP` or Paxos USDP.
export const SUSDP_KEY = 'SUSDP'
export const SUSDP_GROUP_KEY = 'Staked USDp::sUSDp'

export const parallelFetcher: YieldFetcher = {
  label: 'PARALLEL_SUSDP',
  fetch: async () => {
    let apr = 0
    for (const { chain, address } of SUSDP_VAULTS) {
      try {
        const [rate] = (await multicallRetryUniversal({
          chain,
          abi: RATE_ABI,
          calls: [{ address, name: 'rate', params: [] }],
          allowFailure: false,
        })) as [bigint]
        apr = aprFromRate(rate)
        break
      } catch {
        // keeper-synced rate — identical everywhere, try the next chain
      }
    }
    return { [SUSDP_KEY]: apr, [SUSDP_GROUP_KEY]: apr }
  },
}
